import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowRight, ChevronLeft, ChevronRight, Film, Heart, Info, Mic, Play, RefreshCw, Search as SearchIcon, Server, Star, X } from 'lucide-react'
import { BrowseScreen } from './components/BrowseScreen'
import { DetailOverlay } from './components/DetailOverlay'
import { MediaCard } from './components/MediaCard'
import { AdminOverlay } from './components/AdminOverlay'
import { GridKeyboard } from './components/GridKeyboard'
import { MediaRow } from './components/MediaRow'
import { Player } from './components/Player'
import { SettingsScreen } from './components/SettingsScreen'
import { SetupScreen } from './components/SetupScreen'
import { SkeletonRow } from './components/SkeletonRow'
import { TopNavigation, type Section } from './components/TopNavigation'
import { TrailerOverlay } from './components/TrailerOverlay'
import { STORAGE } from './constants'
import { useRemoteNavigation } from './hooks/useRemoteNavigation'
import { useTvRemote } from './hooks/useTvRemote'
import { detectCodecCapabilities } from './lib/codecs'
import { applyAppearance, enabledRows, getConfig, saveConfig, type AdminConfig, type HeroConfig, type RowConfig } from './lib/config'
import { enabledAddons, getStreamsFromAddons, normalizeSettings, reconcileLegacySettings, withAddons } from './lib/addons'
import { getCompatibilityEvidence } from './lib/compatibility'
import { detectHdrCapabilities, getStoredHdrCapabilities, type HdrCapabilities } from './lib/hdr'
import { dispatchRemoteKey, nativeRemoteCommand, normalizeRemoteCommand } from './lib/remoteControl'
import {
  boundedRemoteNumber, boundedRemoteText, REMOTE_PROTOCOL_VERSION, streamRemoteKey,
  toRemoteMediaRef, toRemoteMediaSummary, type RemoteCommand, type RemotePlayerState, type RemoteTvState,
} from './lib/remoteProtocol'
import { getCatalog, getMeta, searchCatalog, testServer } from './lib/stremio'
import { bestAllowedStream, describeStream } from './lib/streams'
import { formatRemaining } from './lib/format'
import { nextVideoAfterHistory, releasedThisWeek, titleYears } from './lib/editorial'
import { clearPlaybackSession, getPlaybackSession, savePlaybackSession } from './lib/playbackSession'
import { resumeStartTime, validatedResumeTime } from './lib/playback'
import { applySyncSnapshot, createRemoteSyncSnapshot } from './lib/sync'
import { clearWatchProgress, completeSetup, getLibrary, getMeasuredSpeed, getProgress, getRecentSearches, getSettings, getWatched, isSetupComplete, removeTitleProgress, saveProgress, saveRecentSearch, saveSettings, toggleLibrary } from './lib/storage'
import type { Meta, MetaPreview, ResumeItem, Settings, StremioStream, Video, WatchProgress } from './types'

interface Playback {
  stream: StremioStream
  meta: Meta
  video?: Video
  resumeAt?: number
  resumePlaying?: boolean
  playbackSessionKey: number
}

const SEARCH_META_CACHE = new Map<string, Promise<Meta | null>>()
/** A TV app stays open for days; an unbounded cache holds every title it ever
 * enriched. Oldest insertion goes first, which is also the least recently used
 * here because a hit does not reorder. */
const SEARCH_META_CACHE_LIMIT = 200

function cachedSearchMeta(item: MetaPreview) {
  const key = `${item.addonUrl || ''}:${item.type}:${item.id}`
  let request = SEARCH_META_CACHE.get(key)
  if (!request) {
    while (SEARCH_META_CACHE.size >= SEARCH_META_CACHE_LIMIT) {
      const oldest = SEARCH_META_CACHE.keys().next().value
      if (oldest === undefined) break
      SEARCH_META_CACHE.delete(oldest)
    }
    request = getMeta(item.type, item.id, item.addonUrl)
      .catch(() => {
        SEARCH_META_CACHE.delete(key)
        return null
      })
    SEARCH_META_CACHE.set(key, request)
  }
  return request
}

function episodeStreamKey(settings: Settings, meta: Meta, video: Video) {
  const providers = enabledAddons(settings, 'stream')
    .map((addon) => `${addon.priority}:${addon.url}`).join('|')
  return `${meta.type}:${video.id}:${providers}`
}

function App() {
  const [section, setSection] = useState<Section>(() => {
    const saved = localStorage.getItem(STORAGE.lastSection) as Section | null
    return saved && ['home', 'movies', 'series', 'search', 'library', 'settings'].includes(saved) ? saved : 'home'
  })
  const [config, setConfig] = useState<AdminConfig>(getConfig)
  const [adminOpen, setAdminOpen] = useState(false)
  const [heroIndex, setHeroIndex] = useState(0)
  const [movies, setMovies] = useState<MetaPreview[]>([])
  const [series, setSeries] = useState<MetaPreview[]>([])
  const [newThisWeek, setNewThisWeek] = useState<MetaPreview[]>([])
  const [nextUpItems, setNextUpItems] = useState<ResumeItem[]>([])
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [catalogError, setCatalogError] = useState('')
  const [selected, setSelected] = useState<MetaPreview | null>(null)
  const [settings, setSettings] = useState<Settings>(getSettings)
  const [library, setLibrary] = useState<MetaPreview[]>(getLibrary)
  const [progress, setProgress] = useState<WatchProgress[]>(getProgress)
  const playbackSequence = useRef(0)
  const [playback, setPlayback] = useState<Playback | null>(() => {
    const settings = getSettings()
    if (!settings.restorePlayback || !getConfig().features.resumePrompt) return null
    const session = getPlaybackSession()
    if (!session) return null
    const resumeAt = validatedResumeTime(session.currentTime, session.duration, session.video?.creditsStart)
    if (session.currentTime >= 5 && resumeAt === 0) {
      clearPlaybackSession()
      return null
    }
    return { stream: session.stream, meta: session.meta, video: session.video, resumeAt, resumePlaying: session.playing, playbackSessionKey: 0 }
  })
  const [resolving, setResolving] = useState('')
  const [needsSetup, setNeedsSetup] = useState(() => !isSetupComplete())
  // a source list the player asked us to reopen after a failure
  const [sourceRequest, setSourceRequest] = useState<{ video?: Video } | null>(null)
  const [trailer, setTrailer] = useState<{ youtubeId?: string; directUrl?: string; title: string } | null>(null)
  const [searchSeed, setSearchSeed] = useState('')
  const resolveToken = useRef(0)
  const autoplayToken = useRef(0)
  const autoplayPrefetch = useRef<{ key: string; createdAt: number; promise: Promise<StremioStream[]> }>()
  const [catalogAttempt, setCatalogAttempt] = useState(0)
  const [server, setServer] = useState<{ online: boolean; label: string }>({ online: false, label: 'Checking server' })
  const [serverAttempt, setServerAttempt] = useState(0)
  const [clock, setClock] = useState('')
  const [hdrCapabilities, setHdrCapabilities] = useState<HdrCapabilities>(getStoredHdrCapabilities)
  const [codecCapabilities] = useState(detectCodecCapabilities)
  const [remotePlayerState, setRemotePlayerState] = useState<RemotePlayerState | undefined>()
  const [detailLayer, setDetailLayer] = useState<'details' | 'sources'>('details')
  const hasPlayback = Boolean(playback)

  useRemoteNavigation(`${section}:${selected?.id || ''}:${playback ? 'player' : ''}:${catalogLoading ? 'loading' : 'ready'}`)

  useEffect(() => { localStorage.setItem(STORAGE.lastSection, section) }, [section])

  useEffect(() => {
    // A restored movie gets the network and decoder first. Catalog refreshes
    // are not visible behind Player and can compete with fragile TV startup.
    if (hasPlayback) { setCatalogLoading(false); return }
    let active = true
    setCatalogLoading(true)
    setCatalogError('')
    const year = String(new Date().getFullYear())
    Promise.allSettled([
      getCatalog('movie'), getCatalog('series'),
      getCatalog('movie', 'year', { genre: year }), getCatalog('series', 'year', { genre: year }),
    ]).then(([movieResult, seriesResult, newMoviesResult, newSeriesResult]) => {
      if (!active) return
      if (movieResult.status === 'fulfilled') setMovies(movieResult.value.slice(0, 40))
      if (seriesResult.status === 'fulfilled') setSeries(seriesResult.value.slice(0, 40))
      if (movieResult.status === 'rejected' && seriesResult.status === 'rejected') {
        setCatalogError('Vela could not reach the Stremio metadata service. Check your internet connection.')
      }
      const fresh = [newMoviesResult, newSeriesResult]
        .flatMap((result) => result.status === 'fulfilled' ? result.value : [])
      setNewThisWeek(releasedThisWeek(fresh).slice(0, 18))
      setCatalogLoading(false)
    })
    return () => { active = false }
  }, [catalogAttempt, hasPlayback])

  useEffect(() => {
    if (hasPlayback) return
    let active = true
    testServer(settings.serverUrl)
      .then((version) => active && setServer({ online: true, label: `Server v${version}` }))
      .catch(() => active && setServer({ online: false, label: 'Server offline' }))
    return () => { active = false }
  }, [hasPlayback, serverAttempt, settings.serverUrl])

  useEffect(() => {
    const wake = () => {
      if (document.visibilityState !== 'visible') return
      window.dispatchEvent(new CustomEvent('vela:remote-credentials'))
      if (hasPlayback) return
      setServerAttempt((value) => value + 1)
      setCatalogAttempt((value) => value + 1)
    }
    document.addEventListener('visibilitychange', wake)
    window.addEventListener('online', wake)
    return () => {
      document.removeEventListener('visibilitychange', wake)
      window.removeEventListener('online', wake)
    }
  }, [hasPlayback])

  useEffect(() => {
    const update = () => setClock(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
    update()
    const timer = window.setInterval(update, 30_000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    let active = true
    detectHdrCapabilities().then((value) => active && setHdrCapabilities(value))
    return () => { active = false }
  }, [])

  const progressMap = useMemo(() => {
    // Progress is newest-first. Keep the newest episode for each title rather
    // than letting Object.fromEntries overwrite it with an older episode.
    const values: Record<string, number> = {}
    progress.forEach((item) => {
      const key = `${item.meta.type}:${item.meta.id}`
      if (!(key in values)) values[key] = item.duration ? item.currentTime / item.duration : 0
    })
    return values
  }, [progress])

  useEffect(() => {
    if (hasPlayback) return
    const watched = getWatched()
    const activeSeries = new Set(progress.filter((item) => item.meta.type === 'series').map((item) => item.meta.id))
    const recentSeries = new Map<string, number>()
    Object.entries(watched).forEach(([key, completedAt]) => {
      const match = key.match(/^series:([^:]+):/)
      if (!match || activeSeries.has(match[1])) return
      recentSeries.set(match[1], Math.max(recentSeries.get(match[1]) || 0, completedAt))
    })
    const ids = Array.from(recentSeries).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([id]) => id)
    if (!ids.length) { setNextUpItems([]); return }
    let active = true
    Promise.allSettled(ids.map((id) => getMeta('series', id))).then((results) => {
      if (!active) return
      setNextUpItems(results.flatMap((result) => {
        if (result.status !== 'fulfilled') return []
        const video = nextVideoAfterHistory(result.value, watched)
        return video ? [{ meta: result.value, video, currentTime: 0, duration: 0 }] : []
      }))
    })
    return () => { active = false }
  }, [hasPlayback, progress])
  // one entry per title, keeping the episode actually in progress
  const continueItems = useMemo<ResumeItem[]>(() => {
    const seen = new Set<string>()
    return progress.filter((item) => {
      const key = `${item.meta.type}:${item.meta.id}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    }).map((item) => ({
      meta: item.meta,
      video: item.video,
      currentTime: item.currentTime,
      duration: item.duration,
    }))
  }, [progress])

  // configuration is the single source of truth for what the app shows;
  // appearance is pushed into CSS custom properties so it applies everywhere
  const updateConfig = useCallback((next: AdminConfig) => setConfig(saveConfig(next)), [])
  useEffect(() => applyAppearance(config.appearance), [config.appearance])

  const visibleSections = useMemo(
    () => config.nav.filter((item) => item.enabled).map((item) => item.id),
    [config.nav],
  )
  const homeRows = useMemo(() => enabledRows(config), [config])
  const clearProgress = useCallback(() => setProgress(clearWatchProgress()), [])
  const retryCatalog = useCallback(() => setCatalogAttempt((value) => value + 1), [])

  // a destination that has just been switched off must not stay on screen
  useEffect(() => {
    if (visibleSections.indexOf(section) < 0) setSection('home')
  }, [section, visibleSections])

  const heroPool = useMemo(() => {
    if (!config.hero.enabled) return []
    const pool = config.hero.source === 'movie' ? movies
      : config.hero.source === 'series' ? series
        : movies.flatMap((movie, index) => (series[index] ? [movie, series[index]] : [movie]))
    return pool.filter((item) => item.background || item.poster).slice(0, config.hero.count)
  }, [config.hero, movies, series])

  useEffect(() => setHeroIndex(0), [heroPool.length])

  useEffect(() => {
    if (!config.hero.rotate || heroPool.length < 2) return
    const timer = window.setInterval(
      () => setHeroIndex((value) => (value + 1) % heroPool.length),
      Math.max(3, config.hero.intervalSeconds) * 1000,
    )
    return () => clearInterval(timer)
  }, [config.hero.intervalSeconds, config.hero.rotate, heroPool.length])

  const recommendations = useMemo(() => {
    const watchedIds = new Set(progress.map((item) => `${item.meta.type}:${item.meta.id}`))
    const weights = new Map<string, number>()
    progress.forEach((item) => (item.meta.genre || item.meta.genres || []).forEach((genre) => weights.set(genre, (weights.get(genre) || 0) + 2)))
    library.forEach((item) => (item.genre || item.genres || []).forEach((genre) => weights.set(genre, (weights.get(genre) || 0) + 1)))
    if (!weights.size) return []
    return movies.concat(series)
      .filter((item) => !watchedIds.has(`${item.type}:${item.id}`))
      .map((item) => ({ item, score: (item.genre || item.genres || []).reduce((total, genre) => total + (weights.get(genre) || 0), 0) }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 18)
      .map((entry) => entry.item)
  }, [library, movies, progress, series])

  function changeSection(value: Section) {
    resolveToken.current += 1
    setResolving('')
    setSelected(null)
    setSourceRequest(null)
    if (value === 'search') setSearchSeed('')
    setSection(value)
  }

  function searchFor(query: string) {
    resolveToken.current += 1
    setResolving('')
    setSelected(null)
    setSourceRequest(null)
    setSearchSeed(query)
    setSection('search')
  }

  function handleLibrary(item: MetaPreview) {
    setLibrary(toggleLibrary(item))
  }

  function handleSaveSettings(value: Settings) {
    const addonsChanged = JSON.stringify(value.addons) !== JSON.stringify(settings.addons)
    const legacyChanged = value.torrentioUrl !== settings.torrentioUrl
      || value.torrentioEnabled !== settings.torrentioEnabled
      || value.subtitlesUrl !== settings.subtitlesUrl
    const next = addonsChanged
      ? withAddons(value, value.addons)
      : legacyChanged ? reconcileLegacySettings(settings, value) : normalizeSettings(value)
    saveSettings(next)
    setSettings(next)
  }

  const startPlayback = useCallback((stream: StremioStream, meta: Meta, video?: Video) => {
    autoplayToken.current += 1
    autoplayPrefetch.current = undefined
    const key = progressKey(meta, video)
    const saved = getProgress().find((item) => item.key === key)
    const resumeAt = resumeStartTime(saved, video?.creditsStart, config.features.resumePrompt)
    const next = {
      stream, meta, video, resumeAt, resumePlaying: true,
      playbackSessionKey: playbackSequence.current + 1,
    }
    playbackSequence.current = next.playbackSessionKey
    savePlaybackSession({ stream, meta, video, currentTime: resumeAt, duration: saved?.duration || 0, playing: true, updatedAt: Date.now() })
    setPlayback(next)
  }, [config.features.resumePrompt])

  const stopPlayback = useCallback(() => {
    autoplayToken.current += 1
    autoplayPrefetch.current = undefined
    resolveToken.current += 1
    clearPlaybackSession()
    setPlayback(null)
    // Player's unmount cleanup persists the final watch-progress sample. Clear
    // the lifecycle session again after that synchronous cleanup so an
    // intentional Stop/BACK never reopens on the next webOS launch.
    window.setTimeout(clearPlaybackSession, 0)
  }, [])

  const cancelResolve = useCallback(() => {
    resolveToken.current += 1
    setResolving('')
  }, [])

  /** A local card choice also supersedes metadata work requested by a phone.
   * Without this gate a late phone response could reopen an older title over
   * the title somebody just chose with the Magic Remote. */
  const selectTitle = useCallback((item: MetaPreview) => {
    cancelResolve()
    setSourceRequest(null)
    setSelected(item)
  }, [cancelResolve])

  /**
   * Play means play, everywhere. It resolves the best source this TV can
   * sustain and starts it; choosing a different one is a separate action.
   * Cancelling is real — a stale resolve is dropped, not started late.
   */
  const playTitle = useCallback(async (item: MetaPreview, preferredVideo?: Video, resolvedMeta?: Meta) => {
    const token = resolveToken.current + 1
    resolveToken.current = token
    setResolving(item.name)
    try {
      if (!enabledAddons(settings, 'stream').length) throw new Error('source add-on disabled')
      const meta = resolvedMeta || await getMeta(item.type, item.id, item.addonUrl).catch(() => ({ ...item } as Meta))
      const video = preferredVideo || nextEpisode(meta, getProgress())
      const streams = await getStreamsFromAddons(settings, meta.type, video?.id || meta.id)
      const best = bestAllowedStream(streams, {
        runtime: meta.runtime,
        measuredSpeed: getMeasuredSpeed(),
        hdrCapabilities,
        hdrMode: settings.hdrMode,
        codecCapabilities,
        compatibility: getCompatibilityEvidence(),
      })
      if (resolveToken.current !== token) return
      if (!best) throw new Error('no sources')
      setResolving('')
      startPlayback(best.stream, meta, video)
    } catch {
      if (resolveToken.current !== token) return
      // nothing playable: open the page with its source list already showing,
      // so the next step is visible rather than guessed at
      setResolving('')
      setSelected(item)
      setSourceRequest({ video: preferredVideo })
    }
  }, [codecCapabilities, hdrCapabilities, settings, startPlayback])

  /** The player could not play this file: send them back to the list. */
  const chooseAnotherSource = useCallback(() => {
    if (!playback) return
    const { meta, video } = playback
    stopPlayback()
    setSelected(meta)
    setSourceRequest({ video })
  }, [playback, stopPlayback])

  const switchPlaybackSource = useCallback((stream: StremioStream, currentTime: number, playing = true) => {
    setPlayback((currentPlayback) => {
      if (!currentPlayback) return currentPlayback
      const next = { ...currentPlayback, stream, resumeAt: currentTime, resumePlaying: playing }
      savePlaybackSession({
        stream,
        meta: currentPlayback.meta,
        video: currentPlayback.video,
        currentTime,
        duration: remotePlayerState?.duration || 0,
        playing,
        updatedAt: Date.now(),
      })
      return next
    })
  }, [remotePlayerState?.duration])

  const playEpisodeFromPlayer = useCallback((target: Video) => {
    if (!playback) return
    const meta = playback.meta
    stopPlayback()
    playTitle(meta, target)
  }, [playTitle, playback, stopPlayback])

  useEffect(() => {
    const back = () => {
      // one stack, popped one layer at a time — a nested panel asks first
      if (adminOpen) { setAdminOpen(false); return }
      if (trailer) { setTrailer(null); return }
      if (resolving) { cancelResolve(); return }
      if (playback) { stopPlayback(); return }
      if (selected) {
        // a nested layer (the source panel) cancels the event to claim BACK
        const consumed = !window.dispatchEvent(new CustomEvent('vela:back-layer', { cancelable: true }))
        if (!consumed) setSelected(null)
        return
      }
      if (section !== 'home') setSection('home')
    }
    window.addEventListener('vela:back', back)
    return () => window.removeEventListener('vela:back', back)
  }, [adminOpen, cancelResolve, playback, resolving, section, selected, stopPlayback, trailer])

  function handleProgress(currentTime: number, duration: number, playing?: boolean) {
    if (!playback) return
    const item: WatchProgress = {
      key: progressKey(playback.meta, playback.video),
      meta: {
        id: playback.meta.id,
        type: playback.meta.type,
        name: playback.meta.name,
        poster: playback.meta.poster,
        background: playback.meta.background,
        description: playback.meta.description,
        releaseInfo: playback.meta.releaseInfo,
        year: playback.meta.year,
        imdbRating: playback.meta.imdbRating,
        genre: playback.meta.genre,
        genres: playback.meta.genres,
      },
      video: playback.video,
      currentTime,
      duration,
      updatedAt: Date.now(),
    }
    setProgress(saveProgress(item))
    savePlaybackSession({
      stream: playback.stream,
      meta: playback.meta,
      video: playback.video,
      currentTime,
      duration,
      playing: playing ?? Boolean(remotePlayerState?.playing),
      updatedAt: Date.now(),
    })
    if (settings.autoplayNext && currentTime > 0 && duration > 0 && duration - currentTime <= 45
      && playback.video && playback.meta.videos && enabledAddons(settings, 'stream').length) {
      const currentIndex = playback.meta.videos.findIndex((entry) => entry.id === playback.video?.id)
      const next = currentIndex >= 0 ? playback.meta.videos[currentIndex + 1] : undefined
      if (next) {
        const key = episodeStreamKey(settings, playback.meta, next)
        const cached = autoplayPrefetch.current
        if (!cached || cached.key !== key || Date.now() - cached.createdAt > 2 * 60_000) {
          autoplayPrefetch.current = {
            key,
            createdAt: Date.now(),
            promise: getStreamsFromAddons(settings, playback.meta.type, next.id).catch(() => []),
          }
        }
      }
    }
  }

  async function handleEnded() {
    const token = autoplayToken.current + 1
    autoplayToken.current = token
    if (!playback || !settings.autoplayNext || !enabledAddons(settings, 'stream').length || !playback.video || !playback.meta.videos) {
      stopPlayback()
      return
    }
    const videos = playback.meta.videos
    const currentIndex = videos.findIndex((video) => video.id === playback.video?.id)
    const next = currentIndex >= 0 ? videos[currentIndex + 1] : undefined
    if (!next) {
      stopPlayback()
      return
    }
    try {
      const key = episodeStreamKey(settings, playback.meta, next)
      const prefetched = autoplayPrefetch.current
      let streams = prefetched?.key === key && Date.now() - prefetched.createdAt <= 2 * 60_000
        ? await prefetched.promise : []
      if (autoplayToken.current !== token) return
      if (!streams.length) streams = await getStreamsFromAddons(settings, playback.meta.type, next.id)
      if (autoplayToken.current !== token) return
      const best = bestAllowedStream(streams, {
        runtime: playback.meta.runtime,
        measuredSpeed: getMeasuredSpeed(),
        hdrCapabilities,
        hdrMode: settings.hdrMode,
        codecCapabilities,
        compatibility: getCompatibilityEvidence(),
      })
      if (best) startPlayback(best.stream, playback.meta, next)
      else stopPlayback()
    } catch {
      if (autoplayToken.current === token) stopPlayback()
    }
  }

  /** A phone may name one exact source. Resolve it asynchronously so BACK,
   * Stop or a color-button navigation can genuinely cancel the request rather
   * than waiting for the add-on and briefly starting stale playback. */
  const playRemoteSource = useCallback(async (command: Extract<RemoteCommand, { kind: 'play_source' }>) => {
    const token = resolveToken.current + 1
    resolveToken.current = token
    setResolving('Choosing the requested source')
    let meta: Meta | null = null
    let targetVideo: Video | undefined
    try {
      meta = await getMeta(command.media.type, command.media.id, command.media.addonUrl)
      targetVideo = command.videoId
        ? meta.videos?.find((candidate) => candidate.id === command.videoId)
        : undefined
      if (!enabledAddons(settings, 'stream').length) throw new Error('source add-on disabled')
      const streams = await getStreamsFromAddons(settings, meta.type, targetVideo?.id || meta.id)
      if (resolveToken.current !== token) return
      const selectedStream = streams.find((candidate) => streamRemoteKey(candidate) === command.sourceKey)
      if (!selectedStream) throw new Error('source changed')
      const ranked = describeStream(selectedStream, {
        runtime: meta.runtime,
        measuredSpeed: getMeasuredSpeed(),
        hdrCapabilities,
        hdrMode: settings.hdrMode,
        codecCapabilities,
        compatibility: getCompatibilityEvidence(),
      })
      if (!ranked.allowed) throw new Error('source blocked')
      setResolving('')
      startPlayback(selectedStream, meta, targetVideo)
    } catch {
      if (resolveToken.current !== token) return
      setResolving('')
      if (meta) {
        setSelected(meta)
        setSourceRequest({ video: targetVideo })
      } else {
        setSelected({ ...command.media, name: 'Title unavailable' })
      }
    }
  }, [codecCapabilities, hdrCapabilities, settings, startPlayback])

  const handleRemoteCommand = useCallback(async (incoming: RemoteCommand) => {
    if (incoming.kind === 'key' && playback && (incoming.key === 'red' || incoming.key === 'green' || incoming.key === 'yellow' || incoming.key === 'blue')) {
      window.dispatchEvent(new CustomEvent('vela:player-open-menu', { detail: incoming.key === 'red' ? 'sources' : incoming.key === 'green' ? 'subtitles' : incoming.key === 'yellow' ? 'audio' : 'stats' }))
      return
    }
    const command = normalizeRemoteCommand(incoming)
    if (command.kind === 'navigate') {
      cancelResolve()
      stopPlayback()
      setTrailer(null)
      setSelected(null)
      setSourceRequest(null)
      setSearchSeed('')
      setSection(command.section)
      return
    }
    if (command.kind === 'search') {
      cancelResolve()
      stopPlayback()
      setTrailer(null)
      setSelected(null)
      setSearchSeed(command.query)
      setSection('search')
      return
    }
    if (command.kind === 'key') {
      if (command.key === 'info' && playback) {
        window.dispatchEvent(new CustomEvent('vela:player-show-controls'))
      } else dispatchRemoteKey(command.key)
      return
    }
    if (command.kind === 'player') {
      if (resolving && command.action.kind === 'stop') {
        cancelResolve()
        return
      }
      window.dispatchEvent(new CustomEvent('vela:player-command', { detail: command.action }))
      return
    }
    if (command.kind === 'update_settings') {
      const desired = { ...settings, ...command.settings }
      const next = command.settings.addons
        ? withAddons(desired, command.settings.addons)
        : reconcileLegacySettings(settings, desired)
      saveSettings(next)
      setSettings(next)
      return
    }

    if (command.kind === 'apply_sync') {
      const applied = applySyncSnapshot(JSON.stringify(command.snapshot), settings, command.mode)
      setSettings(applied.settings)
      setLibrary(applied.library)
      setProgress(applied.progress)
      return
    }

    if (command.kind === 'play_source') {
      playRemoteSource(command)
      return
    }

    if (command.kind === 'open_title' || command.kind === 'open_sources' || command.kind === 'play_title' || command.kind === 'toggle_library') {
      cancelResolve()
    }
    const token = resolveToken.current

    try {
      const meta = await getMeta(command.media.type, command.media.id, command.media.addonUrl)
      if (resolveToken.current !== token) return
      const targetVideo = 'videoId' in command && command.videoId
        ? meta.videos?.find((candidate) => candidate.id === command.videoId)
        : undefined
      if (command.kind === 'open_title') {
        stopPlayback()
        setTrailer(null)
        setSourceRequest(null)
        setSelected(meta)
      } else if (command.kind === 'open_sources') {
        stopPlayback()
        setTrailer(null)
        setSelected(meta)
        setSourceRequest({ video: targetVideo })
      } else if (command.kind === 'play_title') {
        playTitle(meta, targetVideo, meta)
      } else if (command.kind === 'toggle_library') {
        setLibrary(toggleLibrary(meta))
      }
    } catch {
      if (resolveToken.current !== token) return
      setResolving('')
      if ('media' in command) {
        setSelected({ id: command.media.id, type: command.media.type, name: 'Title unavailable' })
      }
    }
  }, [cancelResolve, playRemoteSource, playTitle, playback, resolving, settings, stopPlayback])

  useEffect(() => {
    const physicalKey = (event: KeyboardEvent) => {
      // Phone-generated KeyboardEvents are deliberately synthetic. Handling
      // them again here would recurse; real Magic Remote/CDP events are trusted.
      if (!event.isTrusted) return
      const command = nativeRemoteCommand(event.keyCode || event.which)
      if (!command) return
      // Player owns color/media/number keys at capture phase so source,
      // subtitle, audio and diagnostics shortcuts do not become global nav.
      if (playback && [19, 403, 404, 405, 406, 412, 413, 415, 417].includes(event.keyCode || event.which)) return
      event.preventDefault()
      handleRemoteCommand(command)
    }
    window.addEventListener('keydown', physicalKey)
    return () => window.removeEventListener('keydown', physicalKey)
  }, [handleRemoteCommand, playback])

  // Built on demand: only a frame that actually carries the snapshot pays for
  // reading the whole library and progress history back out of storage.
  const remoteSync = useCallback(() => createRemoteSyncSnapshot(settings), [settings])
  const remoteState = useMemo<Omit<RemoteTvState, 'revision' | 'updatedAt'>>(() => ({
    v: REMOTE_PROTOCOL_VERSION,
    screen: needsSetup ? 'setup'
      : playback ? 'player'
        : trailer ? 'trailer'
          : resolving ? 'resolving'
            : selected ? detailLayer
              : section,
    section,
    selected: selected ? toRemoteMediaSummary(selected) : undefined,
    selectedInLibrary: selected ? library.some((item) => item.id === selected.id && item.type === selected.type) : undefined,
    library: library.slice(0, 100).map(toRemoteMediaRef),
    progress: progress.slice(0, 30).map((item) => ({
      media: toRemoteMediaRef(item.meta),
      videoId: item.video?.id ? boundedRemoteText(item.video.id, 240) : undefined,
      currentTime: boundedRemoteNumber(item.currentTime, 0, 86_400),
      duration: boundedRemoteNumber(item.duration, 0, 86_400),
      updatedAt: boundedRemoteNumber(item.updatedAt, 0, 9_999_999_999_999),
    })),
    resolving: resolving ? boundedRemoteText(resolving, 300) : undefined,
    player: playback ? remotePlayerState : undefined,
    hdrCapabilities: {
      display: hdrCapabilities.display,
      hdr10: hdrCapabilities.hdr10,
      hdr10Plus: hdrCapabilities.hdr10Plus,
      dolbyVision: hdrCapabilities.dolbyVision,
      source: boundedRemoteText(hdrCapabilities.source, 200, 'browser probe'),
    },
    sourceContext: {
      addonUrl: boundedRemoteText(settings.torrentioUrl, 500, 'https://invalid.local'),
      enabled: settings.torrentioEnabled,
      measuredSpeed: getMeasuredSpeed(),
      hdrMode: settings.hdrMode,
      codecCapabilities: {
        ...codecCapabilities,
        source: boundedRemoteText(codecCapabilities.source, 200, 'browser probe'),
        testedAt: boundedRemoteNumber(codecCapabilities.testedAt, 0, 9_999_999_999_999),
      },
    },
    settings,
  }), [codecCapabilities, detailLayer, hdrCapabilities, library, needsSetup, playback, progress, remotePlayerState, resolving, section, selected, settings, trailer])
  const remoteConnection = useTvRemote(remoteState, handleRemoteCommand, remoteSync)

  const hero = heroPool[heroIndex] || heroPool[0]

  if (needsSetup) {
    return (
      <SetupScreen
        settings={settings}
        onSave={handleSaveSettings}
        onDone={() => { completeSetup(); setNeedsSetup(false) }}
      />
    )
  }

  return (
    <div className={`app-shell${playback ? ' player-open' : ''}`}>
      <TopNavigation
        section={section}
        sections={visibleSections}
        onChange={changeSection}
        serverOnline={server.online}
        serverLabel={config.appearance.showServerStatus ? server.label : ''}
        clock={config.appearance.showClock ? clock : ''}
      />
      <main className="main-stage">
        {section === 'home' ? (
          <HomeScreen
            hero={hero}
            movies={movies}
            series={series}
            loading={catalogLoading}
            error={catalogError}
            continueItems={continueItems}
            nextUpItems={nextUpItems}
            newThisWeek={newThisWeek}
            recommendations={recommendations}
            rows={homeRows}
            library={library}
            heroConfig={config.hero}
            heroCount={heroPool.length}
            heroIndex={heroIndex}
            progress={progressMap}
            onSelect={selectTitle}
            onPlay={playTitle}
            onRetry={retryCatalog}
            onClearProgress={clearProgress}
          />
        ) : null}
        {section === 'search' ? <SearchScreen initialQuery={searchSeed} trending={movies.concat(series).slice(0, 30)} showKeyboard={config.features.onScreenKeyboard} onSelect={selectTitle} /> : null}
        {section === 'movies' ? <BrowseScreen type="movie" addons={settings.addons} onSelect={selectTitle} /> : null}
        {section === 'series' ? <BrowseScreen type="series" addons={settings.addons} onSelect={selectTitle} /> : null}
        {section === 'library' ? <LibraryScreen items={library} progress={progressMap} onSelect={selectTitle} onBrowse={() => changeSection('movies')} /> : null}
        {section === 'settings' ? <SettingsScreen settings={settings} onSave={handleSaveSettings} remoteConnection={remoteConnection} hdrCapabilities={hdrCapabilities} features={config.features} onOpenAdmin={() => setAdminOpen(true)} /> : null}
      </main>

      {adminOpen ? (
        <AdminOverlay
          config={config}
          settings={settings}
          onChange={updateConfig}
          onSaveSettings={handleSaveSettings}
          onClose={() => setAdminOpen(false)}
        />
      ) : null}

      {selected ? (
        <DetailOverlay
          key={`${selected.type}:${selected.id}:${selected.addonUrl || ''}`}
          item={selected}
          settings={settings}
          config={config}
          hdrCapabilities={hdrCapabilities}
          codecCapabilities={codecCapabilities}
          progress={progress}
          autoSources={sourceRequest}
          inLibrary={library.some((item) => item.id === selected.id && item.type === selected.type)}
          onClose={() => { setSelected(null); setSourceRequest(null) }}
          onToggleLibrary={handleLibrary}
          onResetProgress={(item) => setProgress(removeTitleProgress(item.type, item.id))}
          onPlay={startPlayback}
          onQuickPlay={playTitle}
          onSourcesShown={() => setSourceRequest(null)}
          onSelect={selectTitle}
          onSearch={searchFor}
          onTrailer={(source, title) => setTrailer(/^https?:\/\//i.test(source) ? { directUrl: source, title } : { youtubeId: source, title })}
          onLayerChange={setDetailLayer}
        />
      ) : null}
      {trailer ? <TrailerOverlay {...trailer} onClose={() => setTrailer(null)} /> : null}
      {resolving ? (
        <div className="player">
          <div className="player-loader">
            <div className="orbital-loader"><i /><i /><span className="brand-mark"><b /></span></div>
            <h2>Finding the best source</h2>
            <p>{resolving}</p>
            <div className="retry-actions">
              <button className="secondary-button" data-focusable data-autofocus onClick={cancelResolve}>
                <X size="1.5rem" /> Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {playback ? (
        <Player
          config={config}
          {...playback}
          settings={settings}
          hdrCapabilities={hdrCapabilities}
          codecCapabilities={codecCapabilities}
          onClose={stopPlayback}
          onSwitchSource={switchPlaybackSource}
          onExitToSources={chooseAnotherSource}
          onPlayEpisode={playEpisodeFromPlayer}
          onSettingsChange={handleSaveSettings}
          onProgress={handleProgress}
          onEnded={handleEnded}
          onRemoteState={setRemotePlayerState}
        />
      ) : null}
    </div>
  )
}

function progressKey(meta: MetaPreview, video?: Video) {
  return `${meta.type}:${meta.id}:${video?.id || meta.id}`
}

/** The episode a viewer should land on: the one in progress, else the first unwatched. */
function nextEpisode(meta: Meta, progress: WatchProgress[]): Video | undefined {
  const videos = (meta.videos || []).filter((video) => video.season !== 0)
  if (!videos.length) return undefined
  const byKey = new Map(progress.map((item) => [item.key, item]))
  const completed = getWatched()
  for (let index = 0; index < videos.length; index += 1) {
    const key = progressKey(meta, videos[index])
    if (completed[key]) continue
    const entry = byKey.get(key)
    if (!entry) return videos[index]
    const ratio = entry.duration ? entry.currentTime / entry.duration : 0
    if (ratio < 0.92) return videos[index]
  }
  return videos[videos.length - 1]
}

function HomeScreen({ hero, movies, series, loading, error, continueItems, nextUpItems, newThisWeek, recommendations, rows, library, heroConfig, heroCount, heroIndex, progress, onSelect, onPlay, onRetry, onClearProgress }: {
  hero?: MetaPreview
  movies: MetaPreview[]
  series: MetaPreview[]
  loading: boolean
  error: string
  continueItems: ResumeItem[]
  nextUpItems: ResumeItem[]
  newThisWeek: MetaPreview[]
  recommendations: MetaPreview[]
  rows: RowConfig[]
  library: MetaPreview[]
  heroConfig: HeroConfig
  heroCount: number
  heroIndex: number
  progress: Record<string, number>
  onSelect: (item: MetaPreview) => void
  onPlay: (item: MetaPreview, video?: Video) => void
  onRetry: () => void
  onClearProgress: () => void
}) {
  /**
   * Each configured row resolves to a list of titles; an empty row renders
   * nothing. Resolved once per input change rather than per render: these rows
   * stay mounted behind the player, where a remote-state frame every 750 ms
   * would otherwise re-sort every pool and hand MediaRow a new array each time.
   */
  const rowItems = useMemo(() => {
    const sortedPools = new Map<string, MetaPreview[]>()
    const pool = (row: RowConfig) => {
      const key = `${row.type || 'mixed'}:${row.sort || 'popular'}`
      const cached = sortedPools.get(key)
      if (cached) return cached
      const base = row.type === 'movie' ? movies : row.type === 'series' ? series : movies.concat(series)
      const value = row.sort === 'rating'
        ? base.slice().sort((a, b) => (Number(b.imdbRating) || 0) - (Number(a.imdbRating) || 0))
        : row.sort === 'year'
          ? base.slice().sort((a, b) => titleYears(b).start - titleYears(a).start)
          : base
      sortedPools.set(key, value)
      return value
    }
    return new Map(rows.map((row): [string, MetaPreview[]] => {
      switch (row.kind) {
        case 'newThisWeek': return [row.id, newThisWeek.slice(0, row.limit)]
        case 'recommended': return [row.id, recommendations.slice(0, row.limit)]
        case 'watchlist': return [row.id, library.slice(0, row.limit)]
        case 'genre': return [row.id, pool(row).filter((item) => (item.genre || item.genres || []).indexOf(row.genre || '') >= 0).slice(0, row.limit)]
        case 'catalog': return [row.id, pool(row).slice(0, row.limit)]
        default: return [row.id, []]
      }
    }))
  }, [library, movies, newThisWeek, recommendations, rows, series])

  const continueSlices = useMemo(
    () => new Map(rows.map((row): [string, ResumeItem[]] => [
      row.id,
      row.kind === 'continue' ? continueItems.slice(0, row.limit)
        : row.kind === 'nextUp' ? nextUpItems.slice(0, row.limit) : [],
    ])),
    [continueItems, nextUpItems, rows],
  )
  return (
    <div className="home-screen">
      {hero ? (
        <section className="hero" style={hero.background ? { backgroundImage: `url(${hero.background})` } : undefined}>
          <div className="hero-vignette" />
          <div className="hero-content">
            {hero.logo ? <img className="hero-logo" src={hero.logo} alt={hero.name} /> : <h1>{hero.name}</h1>}
            <div className="hero-meta">
              {hero.imdbRating ? <span><Star size="1.5rem" fill="currentColor" /> {hero.imdbRating}</span> : null}
              <span>{hero.releaseInfo || hero.year}</span>
              <span>{hero.type === 'series' ? 'Series' : 'Movie'}</span>
            </div>
            {heroConfig.showDescription ? <p>{hero.description}</p> : null}
            <div className="hero-actions" data-nav-group="row" data-nav-key="hero">
              <button className="primary-button" data-focusable data-autofocus data-screen-entry="home" data-nav-up-section="home" onClick={() => onPlay(hero)}>
                <Play size="1.75rem" fill="currentColor" /> Play
              </button>
              <button className="glass-button" data-focusable data-nav-up-section="home" onClick={() => onSelect(hero)}>
                <Info size="1.75rem" /> More info
              </button>
              {heroCount > 1 ? (
                <span className="hero-dots" role="img" aria-label={`Title ${heroIndex + 1} of ${heroCount}`}>
                  {Array.from({ length: heroCount }).map((_, index) => (
                    <i className={index === heroIndex ? 'active' : ''} key={index} />
                  ))}
                </span>
              ) : null}
            </div>
          </div>
        </section>
      ) : heroConfig.enabled ? <div className="hero hero-placeholder shimmer" /> : null}
      <div className="content-rows">
        {error ? (
          <div className="catalog-error">
            <Server size="2rem" />
            <span><strong>Content service unavailable</strong>{error}</span>
            <div className="retry-actions" data-nav-group="row" data-nav-key="catalog-retry">
              <button className="secondary-button" data-focusable data-autofocus onClick={onRetry}>
                <RefreshCw size="1.5rem" /> Try again
              </button>
            </div>
          </div>
        ) : null}
        {loading ? <><SkeletonRow /><SkeletonRow /></> : rows.map((row) => {
          if (row.kind === 'continue') {
            return <ContinueRow key={row.id} navKey={`row:${row.id}`} title={row.title} items={continueSlices.get(row.id) || []} onSelect={onSelect} onPlay={onPlay} onClear={onClearProgress} />
          }
          if (row.kind === 'nextUp') {
            return <ContinueRow key={row.id} navKey={`row:${row.id}`} title={row.title} items={continueSlices.get(row.id) || []} onSelect={onSelect} onPlay={onPlay} />
          }
          return <MediaRow key={row.id} title={row.title} items={rowItems.get(row.id) || []} onSelect={onSelect} progress={progress} />
        })}
      </div>
    </div>
  )
}

/**
 * Continue watching is the most-used row on a TV, so it resumes rather than
 * opening a page: the card names the episode, says how much is left, and OK
 * starts it where it was left off.
 */
const ContinueRow = memo(function ContinueRow({ title, navKey, items, onSelect, onPlay, onClear }: {
  title: string
  navKey: string
  items: ResumeItem[]
  onSelect: (item: MetaPreview) => void
  onPlay: (item: MetaPreview, video?: Video) => void
  onClear?: () => void
}) {
  // Derived from `items` alone, so MediaRow can bail out of a re-render that
  // only happened because playback advanced.
  const metas = useMemo(() => items.map((entry) => entry.meta), [items])
  const activate = useCallback((_item: MetaPreview, index: number) => onPlay(items[index].meta, items[index].video), [items, onPlay])
  const sublabel = useCallback((_item: MetaPreview, index: number) => [
    items[index].video?.season && items[index].video?.episode ? `S${items[index].video!.season} E${items[index].video!.episode}` : '',
    items[index].duration ? formatRemaining(items[index].currentTime, items[index].duration) : 'Next episode',
  ].filter(Boolean).join(' \u00b7 '), [items])
  const itemProgress = useCallback((_item: MetaPreview, index: number) => (
    items[index].duration ? items[index].currentTime / items[index].duration : undefined
  ), [items])
  const headerAction = useMemo(
    () => (onClear ? { label: 'Clear watching history', onClick: onClear } : undefined),
    [onClear],
  )

  if (!items.length) return null
  return <MediaRow
    title={title}
    navKey={navKey}
    items={metas}
    onSelect={onSelect}
    onActivate={activate}
    sublabel={sublabel}
    itemProgress={itemProgress}
    headerAction={headerAction}
  />
})

const SEARCH_PAGE_SIZE = 24

function SearchScreen({ initialQuery, trending, showKeyboard, onSelect }: { initialQuery: string; trending: MetaPreview[]; showKeyboard: boolean; onSelect: (item: MetaPreview) => void }) {
  const queryInput = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState(initialQuery)
  const [results, setResults] = useState<MetaPreview[]>([])
  const [loading, setLoading] = useState(false)
  const [recent, setRecent] = useState(getRecentSearches)
  const [page, setPage] = useState(0)
  const [trendingMetadata, setTrendingMetadata] = useState<Meta[]>([])
  const [searchError, setSearchError] = useState('')
  const [attempt, setAttempt] = useState(0)
  const [listening, setListening] = useState(false)
  const speechAvailable = Boolean((window as typeof window & { webkitSpeechRecognition?: new () => {
    lang: string
    interimResults: boolean
    onresult: (event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void
    onerror: () => void
    onend: () => void
    start(): void
  } }).webkitSpeechRecognition)
  const lgVoiceAvailable = Boolean((globalThis as typeof globalThis & { PalmSystem?: unknown }).PalmSystem)
  const trendingKey = trending.map((item) => `${item.type}:${item.id}`).join('|')

  useEffect(() => { if (initialQuery) setQuery(initialQuery) }, [initialQuery])

  // Cast/director/genre search is local enrichment. Fetch the small trending
  // set once, not again for every D-pad letter or phone-provided query.
  useEffect(() => {
    let active = true
    setTrendingMetadata([])
    Promise.all(trending.slice(0, 24).map(cachedSearchMeta)).then((metadata) => {
      if (active) setTrendingMetadata(metadata.filter((entry): entry is Meta => Boolean(entry)))
    })
    return () => { active = false }
  // Keyed on the id string, not the array: a re-render that produces an equal
  // `trending` array must not refetch the whole enrichment set.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendingKey])

  function selectResult(item: MetaPreview) {
    if (query) setRecent(saveRecentSearch(query))
    onSelect(item)
  }

  // The network search depends on the query alone. Cast/director/genre matches
  // are local, so deriving them separately stops the trending metadata arriving
  // mid-flight from clearing the results and issuing the request a second time.
  useEffect(() => {
    setPage(0)
    setResults([])
    setSearchError('')
    if (query.trim().length < 2) {
      setLoading(false)
      return
    }
    setLoading(true)
    let active = true
    const timer = window.setTimeout(() => {
      searchCatalog(query)
        .then((value) => active && setResults(value))
        .catch(() => {
          if (!active) return
          setResults([])
          setSearchError('Search providers are unavailable. Check the connection and try again.')
        })
        .finally(() => active && setLoading(false))
    }, 350)
    return () => { active = false; clearTimeout(timer) }
  }, [attempt, query])

  const related = useMemo(() => {
    const empty = { cast: [] as MetaPreview[], director: [] as MetaPreview[], genre: [] as MetaPreview[] }
    if (query.trim().length < 2) return empty
    const fold = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    const lowered = fold(query.trim())
    return trendingMetadata.reduce((found, meta) => {
      if ((meta.cast || []).some((name) => fold(name).includes(lowered))) found.cast.push(meta)
      const directors = Array.isArray(meta.director) ? meta.director : meta.director ? [meta.director] : []
      if (directors.some((name) => fold(name).includes(lowered))) found.director.push(meta)
      if ((meta.genre || meta.genres || []).some((name) => fold(name).includes(lowered))) found.genre.push(meta)
      return found
    }, empty)
  }, [query, trendingMetadata])

  function listen() {
    const Constructor = (window as typeof window & { webkitSpeechRecognition?: new () => {
      lang: string
      interimResults: boolean
      onresult: (event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void
      onerror: () => void
      onend: () => void
      start(): void
    } }).webkitSpeechRecognition
    if (!Constructor) return
    const recognition = new Constructor()
    recognition.lang = navigator.language || 'en-US'
    recognition.interimResults = false
    recognition.onresult = (event) => setQuery(event.results[0]?.[0]?.transcript || '')
    recognition.onerror = () => setListening(false)
    recognition.onend = () => setListening(false)
    setListening(true)
    recognition.start()
  }

  function openLgVoiceInput() {
    // Consumer webOS does not expose Magic Remote microphone audio to web
    // apps. Its supported path is the native virtual keyboard: once this real
    // input owns focus, the remote's microphone button writes the transcript.
    queryInput.current?.focus()
    queryInput.current?.click()
  }

  const pages = Math.max(1, Math.ceil(results.length / SEARCH_PAGE_SIZE))
  const visibleResults = results.slice(page * SEARCH_PAGE_SIZE, (page + 1) * SEARCH_PAGE_SIZE)

  return (
    <div className="screen search-screen">
      <header className="screen-title"><h1>Search</h1></header>
      <div className="search-box">
        <SearchIcon size="2rem" />
        <input
          ref={queryInput}
          value={query}
          placeholder="Movies, series, actors…"
          aria-label="Search query"
          data-focusable={showKeyboard ? undefined : ''}
          data-autofocus={showKeyboard ? undefined : ''}
          data-screen-entry={showKeyboard ? undefined : 'search'}
          tabIndex={showKeyboard ? -1 : 0}
          onChange={(event) => setQuery(event.target.value)}
        />
        {lgVoiceAvailable ? <button className="voice-search lg-voice-search" data-focusable aria-label="Open LG voice input" onClick={openLgVoiceInput}><Mic size="1.75rem" /> LG voice</button> : null}
        {speechAvailable ? <button className={`voice-search${listening ? ' active' : ''}`} data-focusable aria-label="Search with LG Magic Remote microphone" onClick={listen}><Mic size="1.75rem" /> {listening ? 'Listening…' : 'Voice'}</button> : null}
        {loading ? <i className="small-loader" /> : query ? <small>{results.length} results</small> : null}
      </div>

      <div className={`search-layout${showKeyboard ? '' : ' no-keyboard'}`}>
        {/* typing with a D-pad needs a grid, not the system IME */}
        {showKeyboard ? (
          <GridKeyboard
            entrySection="search"
            upSection="search"
            onKey={(character) => setQuery((value) => value + character)}
            onBackspace={() => setQuery((value) => value.slice(0, -1))}
            onClear={() => setQuery('')}
          />
        ) : null}

        <div>
          {!query ? (
            <div className="search-suggestions">
              <div className="search-intro"><span><Film size="2.25rem" /></span><div><h2>Your next story starts here</h2><p>Results appear as you type.</p></div></div>
              {recent.length ? (
                <section><h3>Recent searches</h3><div className="suggestion-chips" data-nav-group="row" data-nav-key="recent-searches">{recent.map((value) => <button className="filter-button" data-focusable onClick={() => setQuery(value)} key={value}>{value}</button>)}</div></section>
              ) : null}
              {trending.length ? (
                <section><h3>Popular now</h3><div className="compact-media-grid" data-nav-group="grid" data-nav-columns="4" data-nav-key="search-trending">{trending.slice(0, 8).map((item) => <MediaCard key={`${item.type}:${item.id}`} item={item} onSelect={selectResult} />)}</div></section>
              ) : null}
            </div>
          ) : null}
          {query && query.trim().length < 2 ? <div className="search-empty"><h2>Keep typing</h2><p>Enter at least two characters.</p></div> : null}
          {query && !loading && searchError ? (
            <div className="search-empty"><h2>Search unavailable</h2><p>{searchError}</p><button className="secondary-button" data-focusable onClick={() => setAttempt((value) => value + 1)}><RefreshCw size="1.5rem" /> Try again</button></div>
          ) : null}
          {query && !loading && !searchError && !results.length ? (
            <div className="search-empty"><h2>No titles found</h2><p>Try another title or check the spelling.</p></div>
          ) : null}
          {results.length ? (
            <>
              <p className="search-results-title">Results for &ldquo;{query}&rdquo;</p>
              <div className="media-grid" data-nav-group="grid" data-nav-columns="4" data-nav-key={`search-results:${page}`}>
                {visibleResults.map((item) => <MediaCard key={`${item.type}:${item.id}`} item={item} onSelect={selectResult} />)}
              </div>
              {pages > 1 ? <div className="catalog-pager" data-nav-group="row" data-nav-key="search-pages"><button className="secondary-button" data-focusable disabled={page === 0} onClick={() => setPage((value) => Math.max(0, value - 1))}><ChevronLeft size="1.5rem" /> Previous</button><span>Page {page + 1} of {pages}</span><button className="secondary-button icon-after" data-focusable disabled={page >= pages - 1} onClick={() => setPage((value) => Math.min(pages - 1, value + 1))}>Next <ChevronRight size="1.5rem" /></button></div> : null}
            </>
          ) : null}
          {related.cast.length ? <MediaRow title="Cast matches" items={related.cast} onSelect={selectResult} /> : null}
          {related.director.length ? <MediaRow title="Director matches" items={related.director} onSelect={selectResult} /> : null}
          {related.genre.length ? <MediaRow title="Genre matches" items={related.genre} onSelect={selectResult} /> : null}
        </div>
      </div>
    </div>
  )
}

function LibraryScreen({ items, progress, onSelect, onBrowse }: {
  items: MetaPreview[]
  progress: Record<string, number>
  onSelect: (item: MetaPreview) => void
  onBrowse: () => void
}) {
  const [filter, setFilter] = useState<'all' | 'movie' | 'series'>('all')
  const [sort, setSort] = useState<'recent' | 'name' | 'rating'>('recent')
  const visible = items
    .filter((item) => filter === 'all' || item.type === filter)
    .slice()
    .sort((a, b) => sort === 'name'
      ? a.name.localeCompare(b.name)
      : sort === 'rating'
        ? (Number(b.imdbRating) || 0) - (Number(a.imdbRating) || 0)
        : items.indexOf(a) - items.indexOf(b))

  return (
    <div className="screen library-screen">
      <header className="discover-header">
        <div className="screen-title">
          <h1>My library</h1>
          <p>{items.length} {items.length === 1 ? 'title' : 'titles'} saved on this TV.</p>
        </div>
        {items.length ? <div className="filter-tabs" data-nav-group="row" data-nav-key="library-sort">{(['recent', 'name', 'rating'] as const).map((value) => <button className={`filter-button${sort === value ? ' active' : ''}`} data-focusable onClick={() => setSort(value)} key={value}>{value === 'recent' ? 'Recently added' : value === 'name' ? 'A–Z' : 'Rating'}</button>)}</div> : null}
      </header>
      {!items.length ? (
        <div className="search-empty library-empty">
          <span><Heart size="2.75rem" /></span>
          <h2>Your list is waiting</h2>
          <p>Add movies and series from their details page.</p>
          <button className="secondary-button" data-focusable data-autofocus data-screen-entry="library" data-nav-up-section="library" onClick={onBrowse}>
            <ArrowRight size="1.5rem" /> Browse Movies
          </button>
        </div>
      ) : (
        <>
          <div className="filter-tabs library-filters" data-nav-group="row" data-nav-key="library-filter">{(['all', 'movie', 'series'] as const).map((value) => <button className={`filter-button${filter === value ? ' active' : ''}`} data-focusable data-autofocus={value === 'all' ? '' : undefined} data-screen-entry={value === 'all' ? 'library' : undefined} data-nav-up-section="library" onClick={() => setFilter(value)} key={value}>{value === 'all' ? 'All' : value === 'movie' ? 'Movies' : 'Series'}</button>)}</div>
          {visible.length ? <div className="media-grid" data-nav-group="grid" data-nav-columns="6" data-nav-key="library-grid">
            {visible.map((item) => <MediaCard key={`${item.type}:${item.id}`} item={item} onSelect={onSelect} progress={progress[`${item.type}:${item.id}`]} />)}
          </div> : <div className="search-empty"><h2>No saved {filter}</h2><p>Choose All or add something from a title page.</p></div>}
        </>
      )}
    </div>
  )
}

export default App
